import * as React from 'react';
import { Popover } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import getTranslatedText from './getTranslatedText';

const BASE_URL: Record<KnowledgeBase, string> = {
    wikimedia: 'https://www.wikidata.org/wiki/Q',
    capiq: 'https://www.capitaliq.com/CIQDotNet/company.aspx?companyId='
};

function generateEntityLink(knowledgeBase: KnowledgeBase, id: string): string {
    const base = BASE_URL[knowledgeBase];
    return `${base}${id}`;
}

function buildColorWithOpacity(color: string, hovered: string | null, id: string): string {
    const opacity = hovered === null || hovered === id ? 1 : 0.15;
    const chunks = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
    if (chunks === null) return color;

    return `rgba(${parseInt(chunks[1], 16)}, ${parseInt(chunks[2], 16)}, ${parseInt(chunks[3], 16)}, ${opacity})`;
}

const useStyles = makeStyles({
    root: {
        pointerEvents: 'none'
    },
    paper: {
        pointerEvents: 'auto'
    }
});

export default function EntityMark(props: EntityMarkProps): JSX.Element {
    const { annotation, knowledgeBase, popoverMetadata, children, color, hoveredEntity, onHover } = props;
    const classes = useStyles();
    const [isOpen, setIsOpen] = React.useState(false);
    const [mousePosition, setMousePosition] = React.useState<{ x: number; y: number } | null>(null);

    function handleMouseEnter(event: React.MouseEvent): void {
        onHover(annotation.entity_kb_id);
        setIsOpen(true);
        setMousePosition({ x: event.clientX, y: event.clientY });
    }

    function handleMouseLeave(): void {
        onHover(null);
        setIsOpen(false);
        setMousePosition(null);
    }

    const isCapIq = knowledgeBase === 'capiq';
    const markTextColor = hoveredEntity !== null && hoveredEntity !== annotation.entity_kb_id ? { color: 'black' } : {};
    const markColor = color === null ? 'transparent' : buildColorWithOpacity(color, hoveredEntity, annotation.entity_kb_id);

    return (
        <mark style={{ backgroundColor: markColor, ...markTextColor }} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            {children}
            <Popover
                disableScrollLock
                open={isOpen}
                anchorPosition={mousePosition ? { left: mousePosition.x, top: mousePosition.y } : { left: 0, top: 0 }}
                anchorReference="anchorPosition"
                classes={{ root: classes.root, paper: classes.paper }}
                transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <div className="nerd-demo__popover-wrapper">
                    <b>{annotation.text}</b>
                    {popoverMetadata.map(metadata => (
                        <div key={metadata.title} className="nerd-demo__popover-line-item">
                            <b>{metadata.title}</b> {metadata.value}
                        </div>
                    ))}
                    <a
                        href={generateEntityLink(knowledgeBase, annotation.entity_kb_id)}
                        rel="noopener noreferrer"
                        target="_blank"
                        className="button button--primary nerd-demo__link-out-button"
                    >
                        {getTranslatedText(isCapIq ? 'seeCapIqPage' : 'seeWikiPage')}
                    </a>
                </div>
            </Popover>
        </mark>
    );
}
